import React from 'react';
import { Modal, Box, Button, SpaceBetween } from '@cloudscape-design/components';
import { Question } from 'src/types/helios/Question';
import { TranslationFormData } from 'src/validations/translationFormSchema';
import { QUESTION_FIELD_NAMES, QUESTION_FIELD_LABELS } from 'src/contstant/commonLabels';
import {
  getChangedFields,
  hasFieldChanged,
  FieldConfig,
} from 'src/utils/htmlCompareUtils';

/** Field configuration: label and whether it contains HTML */
const FIELD_CONFIG: Record<string, FieldConfig> = {
  title: { label: QUESTION_FIELD_LABELS[QUESTION_FIELD_NAMES.TITLE], isHtml: false },
  questionText: {
    label: QUESTION_FIELD_LABELS[QUESTION_FIELD_NAMES.QUESTION_TEXT],
    isHtml: true,
  },
  auditInstruction: {
    label: QUESTION_FIELD_LABELS[QUESTION_FIELD_NAMES.AUDIT_INSTRUCTION],
    isHtml: true,
  },
  passCriteria: {
    label: QUESTION_FIELD_LABELS[QUESTION_FIELD_NAMES.PASS_CRITERIA],
    isHtml: true,
  },
  failCriteria: {
    label: QUESTION_FIELD_LABELS[QUESTION_FIELD_NAMES.FAIL_CRITERIA],
    isHtml: true,
  },
  justification: {
    label: QUESTION_FIELD_LABELS[QUESTION_FIELD_NAMES.JUSTIFICATION],
    isHtml: true,
  },
  taskName: {
    label: QUESTION_FIELD_LABELS[QUESTION_FIELD_NAMES.TASK_NAME],
    isHtml: false,
  },
  taskDescription: {
    label: QUESTION_FIELD_LABELS[QUESTION_FIELD_NAMES.TASK_DESCRIPTION],
    isHtml: true,
  },
  instruction: {
    label: QUESTION_FIELD_LABELS[QUESTION_FIELD_NAMES.INSTRUCTION],
    isHtml: true,
  },
};

/** Fields that only exist in subQuestions, not at top level */
const SUB_QUESTION_ONLY_FIELDS = new Set(['instruction']);

/** Gets labels for changed top-level fields */
const getTopLevelChanges = (
  initial: TranslationFormData,
  current: TranslationFormData,
): string[] => {
  return getChangedFields(
    FIELD_CONFIG,
    (field) => initial[field as keyof TranslationFormData] as string | undefined,
    (field) => current[field as keyof TranslationFormData] as string | undefined,
    SUB_QUESTION_ONLY_FIELDS,
  );
};

/** Gets labels for changed subQuestion fields */
const getSubQuestionChanges = (
  initial: TranslationFormData,
  current: TranslationFormData,
): string[] => {
  const labels: string[] = [];
  const initialSubs = initial.subQuestions || [];
  const currentSubs = current.subQuestions || [];

  currentSubs.forEach((currentSub, index) => {
    const initialSub = initialSubs[index];
    if (!initialSub) return;

    const prefix = `Sub Question ${index + 1} - `;

    // Check title
    if (hasFieldChanged(initialSub.title, currentSub.title, false)) {
      labels.push(prefix + FIELD_CONFIG.title.label);
    }

    // Check instruction
    if (hasFieldChanged(initialSub.instruction, currentSub.instruction, true)) {
      labels.push(prefix + FIELD_CONFIG.instruction.label);
    }

    // Check taskDetails
    if (currentSub.taskDetails && initialSub.taskDetails) {
      if (
        hasFieldChanged(
          initialSub.taskDetails.taskName,
          currentSub.taskDetails.taskName,
          false,
        )
      ) {
        labels.push(prefix + FIELD_CONFIG.taskName.label);
      }
      if (
        hasFieldChanged(
          initialSub.taskDetails.taskDescription,
          currentSub.taskDetails.taskDescription,
          true,
        )
      ) {
        labels.push(prefix + FIELD_CONFIG.taskDescription.label);
      }
    }
  });

  return labels;
};

/** Compares initial and current form data to find actually changed fields */
const getChangedFieldLabels = (
  initialData: TranslationFormData,
  currentData: TranslationFormData,
): string[] => {
  return [
    ...getTopLevelChanges(initialData, currentData),
    ...getSubQuestionChanges(initialData, currentData),
  ];
};

interface TranslationConfirmationModalProps {
  visible: boolean;
  question: Question;
  languageDisplayName: string;
  initialFormData: TranslationFormData;
  currentFormData: TranslationFormData;
  onConfirm: () => void;
  onCancel: () => void;
}

export const TranslationConfirmationModal: React.FC<
  TranslationConfirmationModalProps
> = ({
  visible,
  question,
  languageDisplayName,
  initialFormData,
  currentFormData,
  onConfirm,
  onCancel,
}) => {
  const changedFields = getChangedFieldLabels(initialFormData, currentFormData);
  const hasChanges = changedFields.length > 0;

  return (
    <Modal
      visible={visible}
      onDismiss={onCancel}
      header="Confirm Translation Changes"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={onCancel}>Cancel</Button>
            <Button variant="primary" onClick={onConfirm} disabled={!hasChanges}>
              Confirm & Save
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <Box>
          <Box variant="strong">Language:</Box> {languageDisplayName}
        </Box>
        <Box>
          <Box variant="strong">Question ID:</Box> {question.questionId} (Version{' '}
          {question.version})
        </Box>
        <Box>
          <Box variant="strong">Translations Overwritten for:</Box>
          {hasChanges ? (
            <Box padding={{ top: 'xs', left: 's' }}>
              <SpaceBetween size="xxs">
                {changedFields.map((field, index) => (
                  <Box key={index}>• {field}</Box>
                ))}
              </SpaceBetween>
            </Box>
          ) : (
            <Box color="text-status-inactive" padding={{ top: 's' }}>
              No changes detected
            </Box>
          )}
        </Box>
      </SpaceBetween>
    </Modal>
  );
};
